import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc } from "firebase/firestore";
import { classifyRole, CATEGORIES } from '../utils/roleTaxonomy';

// --- Iconos ---
const IconTrash = ({isDisabled}) => <svg className={`w-5 h-5 ${isDisabled ? 'text-gray-300' : 'text-gray-400 hover:text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>;
const IconPencil = () => <svg className="w-5 h-5 text-gray-400 hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L13.196 5.196z"></path></svg>;
const IconSave = ({status}) => {
    if (status === 'guardado') {
        return <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>;
    }
    return <svg className={`w-5 h-5 ${status === 'guardando' ? 'text-gray-400 animate-spin' : 'text-gray-400 hover:text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>;
};

const RolesView = ({ db }) => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingStatus, setSavingStatus] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState(null);
    const [roleName, setRoleName] = useState('');
    const [roleDescription, setRoleDescription] = useState('');
    const [roleToDelete, setRoleToDelete] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                if (!db) throw new Error("Database connection is not available.");
                
                const usersSnapshot = await getDocs(collection(db, "users"));
                setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const rolesSnapshot = await getDocs(collection(db, "roles"));
                setRoles(rolesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Could not load data. Please check the console.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [db]);
    
    const filteredUsers = useMemo(() => users.filter(user =>
        (user.displayName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    ).sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email)), [users, searchTerm]);

    const sortedRoles = useMemo(() => [...roles].sort((a, b) => a.name.localeCompare(b.name)), [roles]);
    
    const roleUserCounts = useMemo(() => {
        const counts = {};
        roles.forEach(role => {
            counts[role.name] = users.filter(user => user.roles?.includes(role.name)).length;
        });
        return counts;
    }, [users, roles]);

    // Audit de consolidación (cliente): todos los strings de rol usados por los
    // usuarios, agrupados por categoría del flujo, con sugerencia canónica + conteo.
    const roleAudit = useMemo(() => {
        const counts = {};
        users.forEach(u => (u.roles || []).forEach(r => { counts[r] = (counts[r] || 0) + 1; }));
        const entries = Object.entries(counts).map(([name, count]) => ({ name, count, ...classifyRole(name) }));
        const byCat = {};
        entries.forEach(e => { (byCat[e.category] = byCat[e.category] || []).push(e); });
        Object.values(byCat).forEach(arr => arr.sort((a, b) => b.count - a.count));
        return { byCat, distinct: entries.length, needsWork: entries.filter(e => e.status !== 'canonical').length };
    }, [users]);

    // Roles (docs) agrupados por categoría, para la sección de permisos.
    const rolesByCategory = useMemo(() => {
        const byCat = {};
        sortedRoles.forEach(role => {
            const cat = classifyRole(role.name).category;
            (byCat[cat] = byCat[cat] || []).push(role);
        });
        return byCat;
    }, [sortedRoles]);

    const allPermissions = [
        { id: 'canViewDashboard', label: 'Ver Dashboard' },
        { id: 'canCreateJobs', label: 'Crear/Editar Jobs' },
        { id: 'canCreateQuotes', label: 'Crear Cotizaciones' },
        { id: 'canApproveDiscounts', label: 'Aprobar Descuentos' },
        { id: 'canAccessTradeshowCRM', label: 'Acceder a CRM de Eventos' },
        { id: 'canViewClients', label: 'Ver Clientes' },
        { id: 'canManageClients', label: 'Gestionar Clientes' },
        { id: 'canViewMaterials', label: 'Ver Materiales' },
        { id: 'canManageMaterials', label: 'Gestionar Materiales' },
        { id: 'canManageInventory', label: 'Gestionar Inventario' },
        { id: 'canManageMachines', label: 'Gestionar Máquinas' },
        { id: 'canScanProduction', label: 'Usar Escáner/Buscador' },
        { id: 'canManageQueue', label: 'Gestionar Cola de Trabajo' },
        { id: 'canManageUsers', label: 'Gestionar Usuarios' },
        { id: 'canManageRoles', label: 'Gestionar Roles' },
        { id: 'canManageKiosk', label: 'Gestionar Kiosko' },
        { id: 'canManageInvoicing', label: 'Facturación / Administración' },
        { id: 'canManagePricing', label: 'Configurar Precios' },
    ];

    const handleOpenModal = (role = null) => {
        setRoleToEdit(role);
        setRoleName(role ? role.name : '');
        setRoleDescription(role ? role.description : '');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setRoleToEdit(null);
        setRoleName('');
        setRoleDescription('');
    };

    const handleSaveRole = async (e) => {
        e.preventDefault();
        if (!roleName) return;
        const data = { name: roleName, description: roleDescription };
        try {
            if (roleToEdit) {
                await updateDoc(doc(db, "roles", roleToEdit.id), data);
                setRoles(roles.map(r => r.id === roleToEdit.id ? { ...r, ...data } : r));
            } else {
                const initialPermissions = allPermissions.reduce((acc, perm) => ({ ...acc, [perm.id]: false }), {});
                const newRoleRef = await addDoc(collection(db, "roles"), { ...data, permissions: initialPermissions });
                setRoles([...roles, { id: newRoleRef.id, ...data, permissions: initialPermissions }]);
            }
            handleCloseModal();
        } catch (error) { console.error("Error saving role:", error); }
    };
    
    const handleDeleteRole = async () => {
        if (!roleToDelete || roleUserCounts[roleToDelete.name] > 0) return;
        try {
            await deleteDoc(doc(db, "roles", roleToDelete.id));
            setRoles(roles.filter(r => r.id !== roleToDelete.id));
            setRoleToDelete(null);
        } catch (error) { console.error("Error deleting role:", error); }
    };
    
    const handleRoleChange = (userId, roleName, isChecked) => {
        setUsers(users.map(user => user.id === userId ? { ...user, roles: isChecked ? [...(user.roles || []), roleName] : (user.roles || []).filter(r => r !== roleName) } : user));
    };

    const handleSaveUserRoles = async (userId) => {
        const userToSave = users.find(u => u.id === userId);
        if (!userToSave) return;
        setSavingStatus({ ...savingStatus, [userId]: 'guardando' });
        try {
            await updateDoc(doc(db, "users", userId), { roles: userToSave.roles || [] });
            setSavingStatus({ ...savingStatus, [userId]: 'guardado' });
            setTimeout(() => setSavingStatus(prev => ({ ...prev, [userId]: null })), 2000);
        } catch (error) {
            setSavingStatus({ ...savingStatus, [userId]: 'error' });
        }
    };

    const handlePermissionChange = (roleId, permissionId, isChecked) => {
        setRoles(roles.map(role => role.id === roleId ? { ...role, permissions: { ...(role.permissions || {}), [permissionId]: isChecked } } : role));
    };

    const handleSaveRolePermissions = async (roleId) => {
        const roleToSave = roles.find(r => r.id === roleId);
        if (!roleToSave) return;
        setSavingStatus({ ...savingStatus, [roleId]: 'guardando' });
        try {
            await updateDoc(doc(db, "roles", roleId), { permissions: roleToSave.permissions });
            setSavingStatus({ ...savingStatus, [roleId]: 'guardado' });
            setTimeout(() => setSavingStatus(prev => ({ ...prev, [roleId]: null })), 2000);
        } catch (error) {
            setSavingStatus({ ...savingStatus, [roleId]: 'error' });
        }
    };

    if (isLoading) return <div className="p-4">Cargando...</div>;
    if (error) return <div className="p-4 text-red-500">{error}</div>;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Panel de consolidación (audit): roles en uso → categoría + canónico. */}
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <h2 className="text-xl font-semibold text-gray-700">Consolidación de roles</h2>
                    <span className="text-sm text-gray-500">{roleAudit.distinct} roles en uso · <span className={roleAudit.needsWork ? 'text-amber-600 font-semibold' : ''}>{roleAudit.needsWork} por consolidar</span></span>
                </div>
                <p className="text-xs text-gray-500 mb-4">Roles asignados a usuarios, agrupados por categoría del flujo de trabajo. "→ X" sugiere el nombre canónico; el número es cuántos usuarios lo tienen.</p>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {CATEGORIES.filter(c => roleAudit.byCat[c.id]?.length).map(cat => (
                        <div key={cat.id} className="border rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`w-2.5 h-2.5 rounded-full ${cat.dot}`}></span>
                                <h3 className="font-semibold text-sm text-gray-700">{cat.label}</h3>
                            </div>
                            <ul className="space-y-1.5">
                                {roleAudit.byCat[cat.id].map(e => (
                                    <li key={e.name} className="flex items-center justify-between gap-2 text-sm">
                                        <span className="flex items-center gap-2 min-w-0">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cat.chip}`}>{e.name}</span>
                                            {e.status !== 'canonical' && <span className="text-xs text-gray-400 truncate" title={e.note}>{e.note}</span>}
                                        </span>
                                        <span className="text-xs text-gray-500 whitespace-nowrap">{e.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg overflow-x-auto">
                <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                    <h2 className="text-xl font-semibold text-gray-700">Asignar Roles a Usuarios</h2>
                    <input type="text" placeholder="Buscar usuario..." className="w-full sm:w-64 p-2 border border-gray-300 rounded-md" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                        <tr>
                            <th scope="col" className="px-4 py-2">Usuario</th>
                            {sortedRoles.map(role => (<th key={role.id} scope="col" className="px-4 py-2 text-center">{role.name}</th>))}
                            <th scope="col" className="px-4 py-2 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id} className="bg-white border-b hover:bg-gray-50">
                                <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                                    <div className="font-bold">{user.displayName || user.email}</div>
                                    {user.displayName && <div className="text-xs text-gray-500">{user.email}</div>}
                                </td>
                                {sortedRoles.map(role => (
                                    <td key={role.id} className="px-4 py-2 text-center">
                                        <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" checked={user.roles?.includes(role.name) || false} onChange={(e) => handleRoleChange(user.id, role.name, e.target.checked)} />
                                    </td>
                                ))}
                                <td className="px-4 py-2 text-center">
                                    <button onClick={() => handleSaveUserRoles(user.id)} disabled={savingStatus[user.id] === 'guardando'} className={`px-3 py-1 text-xs font-bold text-white rounded-md transition-colors ${savingStatus[user.id] === 'guardando' ? 'bg-gray-400' : ''} ${savingStatus[user.id] === 'guardado' ? 'bg-green-500' : ''} ${!savingStatus[user.id] || savingStatus[user.id] === 'error' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}>
                                        {savingStatus[user.id] === 'guardando' ? 'Guardando...' : (savingStatus[user.id] === 'guardado' ? 'Guardado' : 'Guardar')}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-700">Gestionar Permisos de Roles</h2>
                    <button onClick={() => handleOpenModal()} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">+ Crear Rol</button>
                </div>
                <div className="space-y-6">
                    {CATEGORIES.filter(c => rolesByCategory[c.id]?.length).map(cat => (
                        <div key={cat.id}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`w-2.5 h-2.5 rounded-full ${cat.dot}`}></span>
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{cat.label}</h3>
                            </div>
                            <div className="space-y-2">
                                {rolesByCategory[cat.id].map(role => {
                                    const userCount = roleUserCounts[role.name] || 0;
                                    const isDeletable = userCount === 0;
                                    return (
                                        <div key={role.id} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <div>
                                                    <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                                                        {role.name}
                                                        <span className="text-sm font-normal text-gray-500">({userCount} {userCount === 1 ? 'usuario' : 'usuarios'})</span>
                                                    </h3>
                                                    <p className="text-xs text-gray-500">{role.description}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <button onClick={() => handleSaveRolePermissions(role.id)} disabled={savingStatus[role.id] === 'guardando'} title="Guardar permisos" className="p-1">
                                                        <IconSave status={savingStatus[role.id]} />
                                                    </button>
                                                    <button onClick={() => handleOpenModal(role)} title="Editar rol" className="p-1"><IconPencil /></button>
                                                    <div title={!isDeletable ? "No se puede borrar un rol con usuarios asignados" : "Borrar rol"}>
                                                        <button onClick={() => isDeletable && setRoleToDelete(role)} disabled={!isDeletable} className="p-1 disabled:cursor-not-allowed">
                                                            <IconTrash isDisabled={!isDeletable} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-2 mt-3">
                                                {allPermissions.map(permission => (
                                                    <div key={permission.id} className="flex items-center">
                                                        <input type="checkbox" id={`perm-${role.id}-${permission.id}`} checked={role.permissions?.[permission.id] || false} onChange={(e) => handlePermissionChange(role.id, permission.id, e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                                                        <label htmlFor={`perm-${role.id}-${permission.id}`} className="ml-2 text-sm">{permission.label}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-2xl font-bold mb-4">{roleToEdit ? 'Editar Rol' : 'Crear Nuevo Rol'}</h2>
                        <form onSubmit={handleSaveRole}>
                            <div className="mb-4">
                                <label htmlFor="roleName" className="block text-sm font-medium text-gray-700">Nombre del Rol</label>
                                <input type="text" id="roleName" value={roleName} onChange={(e) => setRoleName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="roleDescription" className="block text-sm font-medium text-gray-700">Descripción</label>
                                <textarea id="roleDescription" value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" rows="3"></textarea>
                            </div>
                            <div className="flex justify-end gap-4">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                                <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">{roleToEdit ? 'Guardar Cambios' : 'Crear Rol'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {roleToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirmar Eliminación</h2>
                        <p className="text-gray-600 mb-4">
                            ¿Seguro que quieres borrar el rol "<strong>{roleToDelete.name}</strong>"?
                            Esta acción no se puede deshacer.
                        </p>
                        <div className="flex justify-end gap-4">
                            <button type="button" onClick={() => setRoleToDelete(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                            <button type="button" onClick={handleDeleteRole} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">Sí, borrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default RolesView;

